import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { onecProductPhotoUrl } from '../../photos/api/urls'
import { draftKey, operationKey } from './keys'
import { cardFields } from './fields'
import { operationView } from './operationViews'
import { EMPTY_BROWSE } from './productsSlice'
import {
  CATALOG_TARGET,
  NEW_PRODUCT,
  type BarcodeRowVm,
  type ConflictVm,
  type FieldVm,
  type ProductBarcode,
  type ProductBrowseIntent,
  type ProductCandidate,
  type ProductCard,
  type ProductCardVm,
  type ProductCreateVm,
  type ProductDraft,
  type ProductField,
  type ProductOperation,
  type ProductOperationsState,
  type ProductRowVm,
  type ProductsIntentState,
  type ProductsVm,
} from './types'

/**
 * Именованные селекторы фичи (D-061, D-062). Вход — НЕ RootState, а то, что
 * binding выбрал: результаты endpoint-ов (с rid в аргументах) и срезы
 * намерения. Фабрики: один экземпляр на смонтированный binding (D07).
 */
type Envelope<T> = OneCEnvelope<T>

const EMPTY_ROWS: readonly ProductRowVm[] = []
const EMPTY_BARCODES: readonly BarcodeRowVm[] = []
const EMPTY_COMMANDS: ProductCardVm['commands'] = []

export const FIELD_LABELS: Readonly<Record<ProductField, string>> = {
  name: 'Наименование',
  article: 'Артикул',
  barcode: 'Штрихкод',
}

/** Поля результата запроса по отдельности — вход мемоизации, не объект целиком. */
function queryFields<I, T>(pick: (input: I) => QueryResultLike<T>) {
  return [
    (input: I) => pick(input).isUninitialized,
    (input: I) => pick(input).isLoading,
    (input: I) => pick(input).isError,
    (input: I) => pick(input).isSuccess,
    (input: I) => pick(input).error,
    (input: I) => pick(input).data,
  ] as const
}

function viewOf<T>(
  isUninitialized: boolean,
  isLoading: boolean,
  isError: boolean,
  isSuccess: boolean,
  error: QueryResultLike<T>['error'],
  data: T | undefined,
  isEmpty: (data: T) => boolean,
): QueryView {
  return queryView(
    {
      isUninitialized,
      isLoading,
      isError,
      isSuccess,
      ...(error !== undefined ? { error } : {}),
      ...(data !== undefined ? { data } : {}),
    },
    isEmpty,
  )
}

/** Данные — только если вид «готово»: ошибка при старых данных — ошибка (F06). */
const readyData = <T>(view: QueryView, data: T | undefined): T | null =>
  view.status === 'ready' || view.status === 'empty' ? (data ?? null) : null

// --- поиск -----------------------------------------------------------------

export interface ProductsVmInput {
  resourceId: string
  intent: ProductBrowseIntent | undefined
  query: QueryResultLike<Envelope<ProductCandidate[]>>
  /** Наличие фото (`photos-present`): ключи товаров с фото; `null` — не спрашивали. */
  photos: QueryResultLike<Envelope<string[]>> | null
  cameraScanAvailable: boolean
  /** Операции товаров: из них окно берёт «Обновить каталог из 1С» своей базы. */
  operations: ProductOperationsState
}

export const makeSelectProductsVm = () => {
  const selectView = createSelector([...queryFields((input: ProductsVmInput) => input.query)], (u, l, e, s, error, data) =>
    viewOf(u, l, e, s, error, data, (value) => value.data.length === 0),
  )
  const selectPhotoKeys = createSelector([(input: ProductsVmInput) => input.photos?.data], (data) =>
    data === undefined ? null : new Set(data.data.map((key) => key.toLowerCase())),
  )
  const selectRows = createSelector(
    [selectView, (input: ProductsVmInput) => input.query.data, selectPhotoKeys, (input: ProductsVmInput) => input.resourceId],
    (view, data, photos, resourceId): readonly ProductRowVm[] => {
      const found = readyData(view, data)
      if (found === null || found.data.length === 0) return EMPTY_ROWS
      return found.data.map((product) => ({
        refKey: product.refKey,
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        hasPhoto: photos === null ? null : photos.has(product.refKey.toLowerCase()),
        photo: photos?.has(product.refKey.toLowerCase()) === true ? onecProductPhotoUrl(resourceId, product.refKey) : null,
      }))
    },
  )
  return createSelector(
    [
      (input: ProductsVmInput) => input.resourceId,
      (input: ProductsVmInput) => input.intent ?? EMPTY_BROWSE,
      selectView,
      selectRows,
      (input: ProductsVmInput) => input.cameraScanAvailable,
      (input: ProductsVmInput) => input.operations[operationKey(input.resourceId, CATALOG_TARGET, 'catalog-refresh')],
    ],
    (resourceId, intent, query, rows, cameraScanAvailable, refresh): ProductsVm => ({
      resourceId,
      text: intent.text,
      submitted: intent.submitted,
      query,
      rows,
      cameraScanAvailable,
      catalogRefresh: refresh === undefined ? null : operationView(refresh),
    }),
  )
}

// --- карточка ----------------------------------------------------------------

export interface ProductCardVmInput {
  resourceId: string
  refKey: string
  card: QueryResultLike<Envelope<ProductCard>>
  barcodes: QueryResultLike<Envelope<ProductBarcode[]>>
  intent: ProductsIntentState
  operations: ProductOperationsState
}

function fieldVm(field: ProductField, draft: ProductDraft | undefined, server: string | null): FieldVm {
  const mine = draft?.values[field]
  const changed = mine !== undefined
  const conflict =
    changed && draft !== undefined && server !== null && server !== draft.base[field] && server !== mine
  return { value: mine ?? server ?? draft?.base[field] ?? '', server, changed, conflict }
}

function operationsOf(operations: ProductOperationsState, resourceId: string, target: string): ProductOperation[] {
  const prefix = `${operationKey(resourceId, target, '')}`
  return Object.values(operations)
    .filter((operation) => operation.key.startsWith(prefix))
    .sort((left, right) => left.startedAt.localeCompare(right.startedAt))
}

export const makeSelectProductCardVm = () => {
  const selectCardView = createSelector([...queryFields((input: ProductCardVmInput) => input.card)], (u, l, e, s, error, data) =>
    viewOf(u, l, e, s, error, data, () => false),
  )
  const selectBarcodesView = createSelector(
    [...queryFields((input: ProductCardVmInput) => input.barcodes)],
    (u, l, e, s, error, data) => viewOf(u, l, e, s, error, data, (value) => value.data.length === 0),
  )
  const selectDraft = (input: ProductCardVmInput) => input.intent.drafts[draftKey(input.resourceId, input.refKey)]
  const selectOperations = createSelector(
    [(input: ProductCardVmInput) => input.operations, (input: ProductCardVmInput) => input.resourceId, (input: ProductCardVmInput) => input.refKey],
    (operations, resourceId, refKey) => operationsOf(operations, resourceId, refKey),
  )
  const selectBarcodes = createSelector(
    [selectBarcodesView, (input: ProductCardVmInput) => input.barcodes.data, selectOperations],
    (view, data, operations): readonly BarcodeRowVm[] => {
      const found = readyData(view, data)
      if (found === null || found.data.length === 0) return EMPTY_BARCODES
      const busy = new Set(
        operations
          .filter((operation) => operation.payload.kind === 'barcode' && (operation.status === 'sending' || operation.status === 'unknown'))
          .map((operation) => (operation.payload.kind === 'barcode' ? operation.payload.barcode : '')),
      )
      return found.data.map((entry) => ({ barcode: entry.barcode, isMain: entry.isMain, busy: busy.has(entry.barcode) }))
    },
  )
  return createSelector(
    [
      (input: ProductCardVmInput) => input.resourceId,
      (input: ProductCardVmInput) => input.refKey,
      selectCardView,
      (input: ProductCardVmInput) => input.card.data,
      selectDraft,
      selectOperations,
      selectBarcodesView,
      selectBarcodes,
      (input: ProductCardVmInput) => input.barcodes.data?.configurationVersion,
    ],
    (resourceId, refKey, query, data, draft, operations, barcodesQuery, barcodes, barcodesVersion): ProductCardVm => {
      const envelope = readyData(query, data)
      const card = envelope?.data ?? null
      const server = card === null ? null : cardFields(card)
      const form = {
        name: fieldVm('name', draft, server?.name ?? null),
        article: fieldVm('article', draft, server?.article ?? null),
      }
      const conflicts: ConflictVm[] = (['name', 'article'] as const)
        .filter((field) => form[field].conflict)
        .map((field) => ({
          field,
          label: FIELD_LABELS[field],
          server: form[field].server ?? '',
          mine: form[field].value,
          base: draft?.base[field] ?? '',
        }))
      const update = operations.find((operation) => operation.action === 'update')
      const locked = update !== undefined && (update.status === 'sending' || update.status === 'unknown')
      const dirty = draft !== undefined && Object.keys(draft.values).length > 0
      const nameEmpty = form.name.value.trim() === ''
      const saveHint = !dirty
        ? null
        : locked
          ? 'Дождитесь итога прежнего сохранения'
          : conflicts.length > 0
            ? 'Решите, какие значения оставить'
            : nameEmpty
              ? 'Наименование не может быть пустым'
              : card === null
                ? 'Карточка не загружена'
                : null
      const deletion = operations.find((operation) => operation.action === 'delete')
      return {
        resourceId,
        refKey,
        query,
        card,
        form,
        dirty,
        draftStartedAt: dirty ? (draft?.startedAt ?? null) : null,
        conflicts,
        locked,
        canSave: dirty && saveHint === null,
        saveHint,
        commands: operations.length === 0 ? EMPTY_COMMANDS : operations.map(operationView),
        barcodesQuery,
        barcodes,
        configurationVersion: envelope?.configurationVersion ?? barcodesVersion ?? null,
        markedForDeletion:
          deletion?.status === 'succeeded' && deletion.result?.kind === 'deleted' && deletion.result.deleted,
      }
    },
  )
}

// --- создание ----------------------------------------------------------------

export interface ProductCreateVmInput {
  resourceId: string
  intent: ProductsIntentState
  operations: ProductOperationsState
  /** Похожие по `checkedName`; без проверки — неинициализированный запрос. */
  similar: QueryResultLike<Envelope<ProductCandidate[]>>
}

const NEW_BASE = { name: '', article: '', barcode: '' }

export const makeSelectProductCreateVm = () => {
  const selectSimilarView = createSelector(
    [...queryFields((input: ProductCreateVmInput) => input.similar)],
    (u, l, e, s, error, data) => viewOf(u, l, e, s, error, data, (value) => value.data.length === 0),
  )
  const selectSimilar = createSelector(
    [selectSimilarView, (input: ProductCreateVmInput) => input.similar.data],
    (view, data): readonly ProductRowVm[] => {
      const found = readyData(view, data)
      if (found === null || found.data.length === 0) return EMPTY_ROWS
      return found.data.map((product) => ({ ...product, hasPhoto: null }))
    },
  )
  return createSelector(
    [
      (input: ProductCreateVmInput) => input.resourceId,
      (input: ProductCreateVmInput) => input.intent.drafts[draftKey(input.resourceId, NEW_PRODUCT)],
      (input: ProductCreateVmInput) => input.operations[operationKey(input.resourceId, NEW_PRODUCT, 'create')],
      selectSimilarView,
      selectSimilar,
    ],
    (resourceId, draft, operation, similarQuery, similar): ProductCreateVm => {
      const field = (name: ProductField) => fieldVm(name, draft ?? { ...emptyDraft(resourceId) }, null)
      const form = { name: field('name'), article: field('article'), barcode: field('barcode') }
      const name = form.name.value.trim()
      const locked = operation !== undefined && (operation.status === 'sending' || operation.status === 'unknown')
      const checkedName = draft?.checkedName ?? null
      const createHint = locked
        ? 'Дождитесь итога создания'
        : name === ''
          ? 'Введите наименование'
          : checkedName !== name
            ? 'Сначала проверьте похожие товары этой базы'
            : similarQuery.status === 'loading'
              ? 'Проверяем похожие товары…'
              : null
      const created = operation?.status === 'succeeded' && operation.result?.kind === 'created' ? operation.result : null
      return {
        resourceId,
        form,
        dirty: draft !== undefined && Object.keys(draft.values).length > 0,
        similarQuery,
        similar,
        checkedName,
        locked,
        canCreate: createHint === null,
        createHint,
        command: operation === undefined ? null : operationView(operation),
        created: created === null ? null : { refKey: created.refKey, outcome: created.outcome, barcodeWarning: created.barcodeWarning },
      }
    },
  )
}

function emptyDraft(resourceId: string): ProductDraft {
  return { resourceId, target: NEW_PRODUCT, base: NEW_BASE, values: {}, baseVersion: null, checkedName: null, startedAt: '' }
}

/** Намерение поиска одной базы — для binding и снимка вкладки. */
export const selectBrowseIntent = (intent: ProductsIntentState, resourceId: string): ProductBrowseIntent | undefined =>
  intent.browse[resourceId.trim().toLowerCase()]
